
import { Component, Vue } from 'vue-property-decorator';

import axios from 'axios';

import CreateEditCompanyForm from '@/components/CreateEditCompanyForm.vue';
import { ApplicationModule, Company, UserRoleName } from '@/lib/models';

@Component({
  components: {
    CreateEditCompanyForm,
  },
})
export default class EditCompany extends Vue {
  private submitting: boolean = false;
  private company: Company | null = null;

  private created(): void {
    this.getRouteData();
  }

  private async getRouteData(): Promise<void> {
    const url: string = `${process.env.VUE_APP_BACKEND_API_HOST}/companies/${this.$route.params.companyId}`;
    const accessToken: string = await (this as any).$auth0.getAccessTokenSilently();
    axios.get(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }).then((response) => {
      this.company = response.data;
    }).catch((error) => {
      const message: string = error.response?.data?.message ?? error.message;
      this.$bvModal.msgBoxOk(message, {
        title: 'Error Getting Company',
        okVariant: 'danger',
        centered: true,
      }).then(() => {
        this.$router.push({ name: 'companies' });
      });
    });
  }

  private async updateCompany(company: Company): Promise<void> {
    this.submitting = true;
    const url: string = `${process.env.VUE_APP_BACKEND_API_HOST}/companies/${this.company!.id}`;
    const accessToken: string = await (this as any).$auth0.getAccessTokenSilently();
    axios.put(url, company, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }).then((response) => {
      const message: string = `Successfully updated company: ${this.company!.name}`;
      this.$bvModal.msgBoxOk(message, {
        title: 'Company Updated',
        okVariant: 'success',
        centered: true,
      });
    }).catch((error) => {
      const message: string = error.response?.data?.message ?? error.message;
      this.$bvModal.msgBoxOk(message, {
        title: 'Error Updating Company',
        okVariant: 'danger',
        centered: true,
      });
    }).finally(() => {
      this.submitting = false;
    });
  }

  private async fixDBSchema(applicationModule: ApplicationModule): Promise<void> {
    this.submitting = true;
    const url: string = `${process.env.VUE_APP_BACKEND_API_HOST}/companies/fixschemas/${this.company!.id}?applicationModuleId=${applicationModule.id}`;
    const accessToken: string = await (this as any).$auth0.getAccessTokenSilently();
    axios.post(url, {}, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }).then((response) => {
      const message: string = `Successfully fixed DB Schema for module: ${applicationModule.name}`;
      this.$bvModal.msgBoxOk(message, {
        title: 'DB Schema Fixed',
        okVariant: 'success',
        centered: true,
      });
    }).catch((error) => {
      const message: string = error.response?.data?.message ?? error.message;
      this.$bvModal.msgBoxOk(message, {
        title: 'Error Fixing DB Schema',
        okVariant: 'danger',
        centered: true,
      });
    }).finally(() => {
      this.submitting = false;
      this.getRouteData();
    });
  }

  private async addModule(applicationModule: ApplicationModule): Promise<void> {
    this.submitting = true;
    const url: string = `${process.env.VUE_APP_BACKEND_API_HOST}/companies/add-module/${this.company!.id}?applicationModuleId=${applicationModule.id}`;
    const accessToken: string = await (this as any).$auth0.getAccessTokenSilently();
    axios.post(url, {}, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }).then((response) => {
      const message: string = `Successfully added application module: ${applicationModule.name}`;
      this.$bvModal.msgBoxOk(message, {
        title: 'Module Added',
        okVariant: 'success',
        centered: true,
      });
    }).catch((error) => {
      const message: string = error.response?.data?.message ?? error.message;
      this.$bvModal.msgBoxOk(message, {
        title: 'Error Adding Module',
        okVariant: 'danger',
        centered: true,
      });
    }).finally(() => {
      this.submitting = false;
      this.getRouteData();
    });
  }

  private async removeModule(applicationModule: ApplicationModule): Promise<void> {
    this.submitting = true;
    const url: string = `${process.env.VUE_APP_BACKEND_API_HOST}/companies/remove-module/${this.company!.id}?applicationModuleId=${applicationModule.id}`;
    const accessToken: string = await (this as any).$auth0.getAccessTokenSilently();
    axios.post(url, {}, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }).then((response) => {
      const message: string = `Successfully removed application module: ${applicationModule.name}`;
      this.$bvModal.msgBoxOk(message, {
        title: 'Module Removed',
        okVariant: 'success',
        centered: true,
      });
    }).catch((error) => {
      const message: string = error.response?.data?.message ?? error.message;
      this.$bvModal.msgBoxOk(message, {
        title: 'Error Removing Module',
        okVariant: 'danger',
        centered: true,
      });
    }).finally(() => {
      this.submitting = false;
      this.getRouteData();
    });
  }
}
